import { Component, NO_ERRORS_SCHEMA, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CheckBoxModule, InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogCloseResult, DialogService, DialogsModule, WindowCloseResult, WindowModule, WindowRef, WindowService } from '@progress/kendo-angular-dialog';
import { NotificationModule, NotificationService } from '@progress/kendo-angular-notification';
import { KENDO_PAGER, PagerModule } from '@progress/kendo-angular-pager';
import { GridModule, PageChangeEvent, PagerPosition, PagerType } from '@progress/kendo-angular-grid';
import { MulticheckFilterComponent } from '../../filters/multicheck-filter/multicheck-filter.component';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import {
  PlacementReviewStudent,
  PlacementReviewStudentNew,
  PlacementReviewStudentType
} from '../../models/models.lib';
import { LoggerService } from '../../shared/logger.service';
import { MeService } from '../../auth/me.service';
import { ToastrService } from 'ngx-toastr';
import {
  faAddressCard,
  faArrowsRotate,
  faBan, faBookOpen,
  faCopy,
  faEdit, faEye,
  faPlusCircle,
  faPrint,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { CompositeFilterDescriptor, distinct, filterBy, orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { AppEnities, FormModes } from '../../common/constants.lib';
import { MyPlacementReviewService } from '../my-placement-review.service';
import { CasiSwitchComponent } from '../../common-components/casi-switch/casi-switch.component';
import { AssessmentComponent } from '../../assessment-form/assessment.component/assessment.component';
import { JsReportService } from '../../common-services/js-report.service';
import { setTitle } from '../../common/utils.lib';
import { MyPlacementReviewAddAsWin } from '../my-placement-review-add-as-win/my-placement-review-add-as-win';
import { ModalWindowService } from '../../shared/modal-window.service';
import { AssessmentWin } from '../../assessment-form/assessment-win/assessment-win';

const { MY_REVIEW } = AppEnities;

@Component({
  selector: 'casi-my-placement-review-list.component',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule, ReactiveFormsModule,
    FontAwesomeModule,
    InputsModule,
    ButtonsModule,
    DialogsModule,
    WindowModule,
    NotificationModule,
    CheckBoxModule,
    KENDO_PAGER,
    PagerModule,
    GridModule,
    MulticheckFilterComponent,
    CasiSwitchComponent
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './my-placement-review-list.component.html',
  styleUrl: './my-placement-review-list.component.scss'
})
export class MyPlacementReviewListComponent implements OnInit, OnDestroy {

  public view: Observable<PlacementReviewStudent[]> = new Observable<PlacementReviewStudent[]>();
  public rawData: PlacementReviewStudent[] = [];
  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };
  public gridData: PlacementReviewStudent[] = filterBy(this.rawData, this.filter);

  public currentItem: PlacementReviewStudentType = PlacementReviewStudentNew.create({});

  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  public pageSizes = true;
  public previousNext = true;
  public position: PagerPosition = "bottom";
  public pageSize = 10;


  public canUpdate: boolean = false;
  public canCreate: boolean = false;
  public canDestroy: boolean = false;
  public canView: boolean = false;
  public formMode: FormModes = FormModes.NOTSET;

  public editWinOpen: boolean = false;

  private _showTitle = new BehaviorSubject<string>('');
  get showTitle() {
    return this._showTitle.getValue();
  }
  set showTitle(val: string) {
    this._showTitle.next(val);
  }

  @ViewChild("appendTo", { read: ViewContainerRef }) appendTo: ViewContainerRef | undefined;

  private _windowRef: WindowRef | undefined;
  private _subs: Subscription[] = [];


  constructor(
    private logger: LoggerService,
    private notificationService: NotificationService,
    private me: MeService,
    public dataService: MyPlacementReviewService,
    public faIcons: FaIconLibrary,
    private dialogService: DialogService,
    private windowService: WindowService,
    private modalWindowService: ModalWindowService,
    private toastr: ToastrService,
    private jsReportService: JsReportService
  ) {
    faIcons.addIcons(
      faPlusCircle, faAddressCard, faEdit,
      faTrash, faPrint,
      faBan, faCopy, faArrowsRotate, faEye, faBookOpen
    );
  }

  public sort: SortDescriptor[] = [];

  //#region NG Event handlers

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');
    const { can_update, can_create, can_view, can_destroy } = this.me.allICanDo(MY_REVIEW);
    this.canUpdate = can_update;
    this.canCreate = can_create;
    this.canView = can_view;
    this.canDestroy = can_destroy;
    this.logger.debug(`ngOnInit:`, {
      can_create, can_update, can_view, can_destroy
    });
    // if(this.me.email)
    this.dataService.query();

    this._subs.push(this.dataService.subscribe(e => {
      this.logger.debug('ngOnInit:v: ', e);
      this.rawData = e;
      this.loadData();
      // this.onToggleAccordion();
    }));
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  //#endregion

  //#region Grid handlers

  public pageChange(event: PageChangeEvent): void {
    this.logger.debug(`pageChange: `, event);
  }

  onClearFilters(gridName: string) {
    this.logger.debug("onClearFilters: ", gridName);
    this.filter = { logic: "and", filters: [] };
    this.loadData();
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.loadData();
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadData();
  }

  public distinctPrimitive(fieldName: string): any[] {
    this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    return distinct(this.gridData, fieldName).map((item: any) => item[fieldName]).filter(v => v !== null && v !== undefined && v !== '');
  }

  //#endregion

  setMidReviewVisible(dataItem: any): void {
    dataItem.supervisor_mid_review_visible = !dataItem.supervisor_mid_review_visible;
    this.logger.debug('setMidReviewVisible:dataItem: ', dataItem);
    this.currentItem = PlacementReviewStudent.create(dataItem);
    this.dataService.update(this.currentItem.id, this.currentItem)
      .then(resp => {
        this.logger.debug('saveActive:update:resp: ', resp);
        this.notify(dataItem.supervisor_mid_review_visible ? "Assessment visible to student" : "Assessment not visible to student");
        this.closeWindowRef();
        this.dataService.query();
      })
      .catch(err => {
        this.logger.error('setMidReviewVisible:update:err: ', err);
        this.notify("Error Updating Student", true);
      });
  }

  setEndReviewVisible(dataItem: any): void {
    dataItem.supervisor_end_review_visible = !dataItem.supervisor_end_review_visible;
    this.logger.debug('setEndReviewVisible:dataItem: ', dataItem);
    this.currentItem = PlacementReviewStudent.create(dataItem);
    this.dataService.update(this.currentItem.id, this.currentItem)
      .then(resp => {
        this.logger.debug('saveActive:update:resp: ', resp);
        this.notify(dataItem.supervisor_end_review_visible ? "Assessment visible to student" : "Assessment not visible to student");
        this.closeWindowRef();
        this.dataService.query();
      })
      .catch(err => {
        this.logger.error('setMidReviewVisible:update:err: ', err);
        this.notify("Error Updating Student", true);
      });
  }

  public viewAssessment(dataItem: any, field: string): void {
    this.logger.debug("viewAssessment: ", { dataItem, field });
    this.openAssessmentDialog(dataItem, field, true);
  }

  onEditClick(dataItem: any, titleBar: TemplateRef<unknown>): void {
    this.logger.debug("onEditClick: ", dataItem);
    this.currentItem = PlacementReviewStudent.create(dataItem);
    this.logger.debug("onEditClick:currentItem ", this.currentItem);
    this.openAddEditDialog(this.currentItem, titleBar);
  }

  private openAddEditDialog(dataItem: PlacementReviewStudentType, titleBar: TemplateRef<unknown>): void {
    this.logger.debug("openAddEditDialog...");
    this.closeWindowRef();
    this.showTitle = setTitle(this.formMode, 'EDIT PLACEMENT INFORMATION');
    this._windowRef = this.modalWindowService.open({
      titleBarContent: titleBar,
      content: MyPlacementReviewAddAsWin,
      minWidth: 320,
      width: 1000,
    });
    // const dialogRef = this.dialogService.open({
    //   content: MyPlacementReviewAddComponent,
    //   minWidth: 320,
    //   width: 1000,
    // });
    this.setWinInstanceInputs(dataItem);
    // const contentRef = dialogRef.content.instance as MyPlacementReviewAddComponent;
    // contentRef.title = 'EDIT PLACEMENT INFORMATION';
    // contentRef.myReview = dataItem;

    this._windowRef.content.instance.submitForm.subscribe((formData: FormGroup) => {
      this.logger.debug('openAddEditDialog:formData: ', formData);
      this.save(formData.value);
    });

    this._windowRef.result.subscribe((r: any) => {
      this.logger.debug('openAddEditDialog:result:', r);
      // if (r instanceof DialogCloseResult) {
      if (r instanceof WindowCloseResult) {
        this.closeWindowRef();
      }
    });

    this._windowRef.content.instance.onCancel.subscribe(() => {
      this.logger.debug('openAddEditDialog:onCancel');
      this.closeWindowRef();
    });

    // this.editWinOpen = true;

  }

  private setWinInstanceInputs(dataItem: PlacementReviewStudentType): void {
    if (this._windowRef) {
      const inputs = this._windowRef.content.instance;
      inputs.title = 'EDIT PLACEMENT INFORMATION';
      inputs.myReview = dataItem;
      this.logger.debug('setWinInstanceInputs: ', inputs);
    }
  }

  private handleClose(data: any): void {
    this.logger.debug('handleClose...', data);
    this.save(data);
  }

  private save(myReview: any): void {
    this.logger.debug('save...', myReview);
    this.dataService.update(myReview.id, myReview)
      .then(resp => {
        this.logger.debug('save:update:resp: ', resp);
        this.notify("Assessment Updated");
        this.closeWindowRef();
        this.dataService.query();
      })
      .catch(err => {
        this.logger.error('save:update:err: ', err);
        this.notify("Error Updating Assessment", true);
      });
  }

  openAssessment(dataItem: any, field: string): void {
    this.logger.debug("openAssessment: ", { dataItem, field });
    this.openAssessmentDialog(dataItem, field, false);
  }

  public printReport(dataItem: any, field: string): void {
    let reportWindow = window.open("about:blank");
    let title = `${this.getReportTitle(field)}`;
    let obj = { id: dataItem.getAssessmentId(field as keyof PlacementReviewStudent), title }
    this.jsReportService.showReportSpinnerPage(reportWindow);
    this.jsReportService.printAssessmentReport(obj)
      .then(report => {
        const url = URL.createObjectURL(report);
        this.jsReportService.generateReport(reportWindow, title, url);
      })
      .catch(e => {
        this.logger.error("printPDF", e);
        this.jsReportService.showReportErrorPage(reportWindow);
      });
  }

  private setAssessmentWinInstanceInputs(dataItem: any, field: string, isDisabled: boolean): void {
    if (this._windowRef) {
      const inputs = this._windowRef.content.instance;
      inputs.title = "PLACEMENT REVIEW";
      inputs.myReview = dataItem;
      inputs.field = field;
      inputs.isDisabled = isDisabled;
      this.logger.debug('setAssessmentWinInstanceInputs: ', inputs);
    }
  }

  private openAssessmentDialog(dataItem: any, field: string, isDisabled: boolean): void {
    this.logger.debug("openAssessmentDialog: ", { dataItem, field });
    this.closeWindowRef();

    this._windowRef = this.modalWindowService.open({
      title: 'PLACEMENT REVIEW',
      content: AssessmentWin,
      cssClass: 'full-width-window'
    });

    this.setAssessmentWinInstanceInputs(dataItem, field, isDisabled);

    this._windowRef.result.subscribe((r: any) => {
      this.logger.debug('openAssessmentDialog:result:', r);
      // if (r instanceof DialogCloseResult) {
      if (r instanceof WindowCloseResult) {
        this.closeWindowRef();
        this.dataService.query();
      }
    });

    this._windowRef.content.instance.onCancel.subscribe(() => {
      this.logger.debug('openAssessmentDialog:onClose');
      this.closeWindowRef();
    });

  }

  private getReportTitle(field: string): string {
    const map: Record<string, string> = {
      start: 'Start Placement',
      mid: 'Mid Placement',
      end: 'End Placement'
    };
    const key = Object.keys(map).find(k => field.includes(k));
    return key ? map[key] : '';
  }

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }


  private closeWindowRef(): void {
    if (this._windowRef) {
      this.modalWindowService.close(this._windowRef);
      this._windowRef = undefined;
    }
    // this.editWinOpen = false;
  }

  private loadData(): void {
    this.gridData = orderBy(filterBy(this.rawData, this.filter), this.sort);
    this.pageSize = this.gridData.length;
  }
}


